"use client"

import type Dexie from "dexie"
import { useLiveQuery } from "dexie-react-hooks"
import { useRef } from "react"
import { offlineDb } from "../db"
import type { SyncMeta } from "../types"

const SYNC_META_TABLE = "_syncMeta"

interface SyncMetaRow extends SyncMeta {
  id: string
}

/**
 * True first boot detector. The verdict is **locked in at the first Dexie
 * resolution** and never changes for the lifetime of the hook instance.
 *
 * Cold-start syncs every critical resource in parallel inside a wave, and
 * the engine stamps `lastFullFetchAt` per resource as each one finishes
 * its full fetch (see `SyncEngine.fullFetch`). A naive "no critical has
 * lastFullFetchAt" check would flip false the moment ONE small resource
 * lands — yanking the boot overlay down while 13/14 are still pulling.
 * Locking the verdict at first read keeps the overlay up until the
 * consumer dismisses it via `criticalReady`.
 *
 * Rule at lock time: it's a first boot iff every listed resource either has
 * no `_syncMeta` row yet or has `lastFullFetchAt == null`. Any single
 * critical resource with a completed full fetch from a prior session
 * means this is a returning user — verdict locks to `false`.
 *
 * Pre-resolution (Dexie hasn't returned yet) we return `false` — assume a
 * returning user until the local `_syncMeta` rows prove otherwise. Returning
 * the optimistic `true` here made the boot overlay flash on EVERY refresh:
 * for the tens of milliseconds before Dexie resolved, a returning user with a
 * fully-populated cache still saw `firstBoot=true` + `criticalReady=false`.
 * Defaulting to `false` costs a genuine cold start only that same sub-100ms
 * delay before the overlay appears (during which there's no data to show
 * anyway), and keeps catch-up refreshes overlay-free.
 */
export function useIsFirstBoot(
  criticalResources: readonly string[],
  db: Dexie = offlineDb
): boolean {
  const verdictRef = useRef<boolean | null>(null)

  const rows = useLiveQuery(
    () =>
      db
        .table(SYNC_META_TABLE)
        .bulkGet(criticalResources as string[]) as Promise<
        (SyncMetaRow | undefined)[]
      >,
    [criticalResources.join("|"), db],
    undefined as (SyncMetaRow | undefined)[] | undefined
  )

  if (rows === undefined) return verdictRef.current ?? false

  if (verdictRef.current === null) {
    const hasAnyCompletedFullFetch = rows.some(
      (row) => row != null && row.lastFullFetchAt != null
    )
    verdictRef.current = !hasAnyCompletedFullFetch
  }

  return verdictRef.current
}
